import type { KeyboardEvent } from 'react';
import { SwitchVisual } from '~/components/product/switch-visual';
import { cn } from '~/lib/utils';

export interface FlagItem {
	key: string;
	label: string;
	help?: string;
	checked: boolean;
	disabled?: boolean;
}

interface FlagGridProps {
	flags: FlagItem[];
	onToggle: (key: string, checked: boolean) => void;
	/** Column count for the responsive grid. Default 2. */
	columns?: number;
}

const COLS: Record<number, string> = {
	1: 'sm:grid-cols-1',
	2: 'sm:grid-cols-2',
	3: 'sm:grid-cols-2 lg:grid-cols-3'
};

/** Compact toggle row matching the HTML `.flag-row`: label + optional help + switch, row tints when on. */
function FlagRow({ flag, onToggle }: { flag: FlagItem; onToggle: (key: string, checked: boolean) => void }) {
	const toggle = () => {
		if (!flag.disabled) onToggle(flag.key, !flag.checked);
	};
	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === ' ' || e.key === 'Enter') {
			e.preventDefault();
			toggle();
		}
	};
	return (
		<div
			role="switch"
			aria-checked={flag.checked}
			aria-disabled={flag.disabled}
			tabIndex={flag.disabled ? -1 : 0}
			onClick={toggle}
			onKeyDown={onKeyDown}
			className={cn(
				'flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-left transition-colors',
				flag.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
				flag.checked ? 'border-brand-border-subtle bg-brand-subtle' : 'border-border-subtle bg-background'
			)}>
			<span className="min-w-0 flex-1">
				<span className="block text-sm font-medium text-foreground">{flag.label}</span>
				{flag.help && <span className="mt-0.5 block text-[11px] leading-tight text-foreground-muted">{flag.help}</span>}
			</span>
			<SwitchVisual checked={flag.checked} disabled={flag.disabled} />
		</div>
	);
}

/** Responsive grid of compact flag toggles — used by the Basic tab "Visibility & flags" and quick-edit. */
export function FlagGrid({ flags, onToggle, columns = 2 }: FlagGridProps) {
	return (
		<div className={cn('grid grid-cols-1 gap-3', COLS[columns] ?? COLS[2])}>
			{flags.map(flag => (
				<FlagRow key={flag.key} flag={flag} onToggle={onToggle} />
			))}
		</div>
	);
}
